/**
 * Price rendering — the components every priced surface shares.
 *
 * WHY THIS FILE EXISTS RATHER THAN A PRICE PER CARD. R6.3 requires the catalogue's course
 * card and its path card to carry the price "in the same slot with the same component",
 * for the reason `PathCard.tsx:18-21` already gives about the two cards: "A card that is
 * NEARLY the same shape as the one a click away reads as a mistake." A price is the one
 * fact on those cards that a reader compares BETWEEN them, so two renderings of it is the
 * worst place in the product for a small difference.
 *
 * NOTHING HERE FORMATS MONEY. Every string comes from `shared/lib/pricing.ts` —
 * `priceLabel`, `processingNote`, `comparisonLabel`, `PRICE_COPY` — because that file is the
 * only place in the app allowed to turn minor units into characters (R5.1, AC-029). These
 * components decide LAYOUT and nothing else.
 *
 * NOTHING HERE DECIDES A STATE EITHER. `buyState` resolves the six-state machine once in
 * `pricing.ts` and the call sites switch on the answer (R6.10); these components render the
 * price, the disclosure and the refusal, and never the button.
 *
 * NO PROMOTION CODE, ANYWHERE (D4, R5.9, AC-003d). There is no code field, no "have a
 * code?" affordance and no code-attributable saving in this file. The strike-through below
 * is a BUNDLE comparison between two prices this app computed, and it is only rendered when
 * `pricing.ts` says the saving is real.
 */

import type { ReactNode } from 'react';
import { Icon } from './prototype';
import { Banner } from './index';
import {
  comparisonLabel,
  formatMoney,
  gateNotice,
  PRICE_COPY,
  priceLabel,
  processingNote,
  type PriceView,
  type TenantPayments,
} from '../lib/pricing';
import './price.css';

/**
 * The price on a CARD: one line, no disclosure, no comparison.
 *
 * `Free`, `RM 131.00` or `Price unavailable`, muted for the last one — the card is still
 * worth opening (R6.3), so the unavailable state is neutral and not an alarm.
 *
 * `omitUnknown` exists for Landing alone (R6.5), which is a marketing page with no buy
 * control to mislabel: "Price unavailable" there is noise on a page where the reader has not
 * yet asked. It is safe there and only there.
 */
export function PriceTag({
  view,
  omitUnknown = false,
}: {
  view: PriceView;
  omitUnknown?: boolean;
}) {
  if (view.mode === 'unknown' && omitUnknown) return null;

  return (
    <span className="price-tag" data-mode={view.mode}>
      {view.mode === 'paid' && <Icon name="sell" />}
      {priceLabel(view)}
    </span>
  );
}

/**
 * The price BLOCK on a detail page: the three lines R6.9 requires, always all three.
 *
 * A struck comparison is rendered only when `pricing.ts` reports a real saving, and it
 * carries `comparisonLabel` as its accessible name (NFR-A1) — a screen reader announcing
 * two numbers with no relation between them is worse than one number. When there is no
 * saving the buy-separately total is still STATED IN WORDS, because a bundle that costs more
 * than its parts is a fact the learner is entitled to (R5.5, AC-011).
 */
export function PriceBlock({ view }: { view: PriceView }) {
  if (view.mode === 'free') return null;

  if (view.mode !== 'paid' || view.totalMinor === null) {
    return (
      <div className="price-block" data-mode="unknown">
        <p className="price-block__amount price-block__amount--muted">{PRICE_COPY.unavailable}</p>
        <p className="price-block__note">{view.reason ?? PRICE_COPY.nothingCharged}</p>
      </div>
    );
  }

  const label = comparisonLabel(view);

  return (
    <div className="price-block" data-mode="paid">
      {view.wasMinor !== null && view.currency && label ? (
        <p className="price-block__compare">
          {/* One element carries the relation for a screen reader; the two numbers inside
              are hidden from it so the pair is announced once, in order, with "was … now". */}
          <span aria-label={label}>
            <s aria-hidden="true">{formatMoney(view.wasMinor, view.currency)}</s>{' '}
            <strong className="price-block__amount" aria-hidden="true">
              {priceLabel(view)}
            </strong>
          </span>
          {view.savingMinor !== null && (
            <span className="price-block__saving">
              {PRICE_COPY.save} {formatMoney(view.savingMinor, view.currency)}
            </span>
          )}
        </p>
      ) : (
        <p className="price-block__amount">{priceLabel(view)}</p>
      )}

      <ProcessingNote view={view} />
    </div>
  );
}

/**
 * The bundle comparison on a CARD: the struck buy-separately total and the saving (R6.8).
 *
 * `PriceTag` states the amount; this states what that amount is cheaper THAN. They are two
 * components rather than one prop because only pathways have anything to compare against —
 * courses carry no discount in this spec (R6.3) — and a `showSaving` flag on the shared tag
 * would put a branch on every course card that can never be true.
 *
 * NOTHING IS RENDERED UNLESS THE SAVING IS REAL. `pricing.ts` sets `wasMinor`/`savingMinor`
 * only when the server reported a saving AND the separate total genuinely exceeds the bundle,
 * so a `fixed` bundle priced ABOVE its parts reaches here with both null and gets no
 * strike-through and no claim (R5.5, AC-011). The buy-separately figure is still stated in
 * words on the path's own page, where there is room for it.
 *
 * The accessible name is `comparisonLabel`'s "was … now …" for the reason NFR-A1 gives: a
 * screen reader announcing two bare numbers with no relation between them is worse than one
 * number, because the listener hears the higher one and cannot tell which applies to them.
 */
export function CardSaving({ view }: { view: PriceView }) {
  const label = comparisonLabel(view);
  if (view.mode !== 'paid' || view.wasMinor === null || !view.currency || !label) return null;

  return (
    <span className="price-tag__compare">
      <span aria-label={label}>
        <s aria-hidden="true">{formatMoney(view.wasMinor, view.currency)}</s>
      </span>
      {view.savingMinor !== null && (
        <span className="price-block__saving">
          {PRICE_COPY.save} {formatMoney(view.savingMinor, view.currency)}
        </span>
      )}
    </span>
  );
}

/**
 * *"Includes RM 6.00 payment processing. This is the full amount you will be charged."*
 *
 * UNCONDITIONAL beside every paid price (R5.4). The words are the only ones permitted: the
 * markup covers the ACADEMY's Stripe charge and settles to the academy, and Mereka takes no
 * commission and receives none of it (D5). It is never described as a platform fee, a
 * service fee, a commission, a Mereka charge or tax, and it is never shown as a line to be
 * added later — there is no later, because this number IS the Stripe Price (D3).
 */
export function ProcessingNote({ view }: { view: PriceView }) {
  const note = processingNote(view);
  if (!note) return null;
  return <p className="price-block__note">{note}</p>;
}

/**
 * "Bought separately: RM 539.64" — the honest comparison, in words, when nothing is struck.
 *
 * Rendered on the pathway detail page in every paid state (R2.3, R6.9): a `fixed` bundle
 * priced ABOVE its parts must be visible as such rather than quietly printing a saving of
 * zero.
 */
export function SeparatelyNote({
  separateMinor,
  currency,
}: {
  separateMinor: number | null;
  currency: string | null;
}) {
  if (separateMinor === null || separateMinor <= 0 || !currency) return null;
  return (
    <p className="price-block__note">
      {PRICE_COPY.boughtSeparately}: {formatMoney(separateMinor, currency)}
    </p>
  );
}

/**
 * The refusal that replaces a buy control (R6.10 `unavailable`, NFR-A2).
 *
 * The button is never HIDDEN — the precedent and its argument are at
 * `CourseHome.tsx:903-907`: "a button that vanishes leaves someone hunting for it; one that
 * is visibly unavailable, with the reason directly beneath, explains itself." So this is a
 * warning banner in the button's place, carrying the reason and the fact that nothing has
 * been charged. "Price unavailable" is never the only thing on screen.
 */
export function UnavailableNotice({
  reason,
  fallback,
}: {
  reason: string | null;
  fallback: string;
}) {
  return (
    <Banner tone="warning">
      <strong>{reason ?? fallback}</strong>{' '}
      {(reason ?? fallback).includes(PRICE_COPY.nothingCharged) ? null : PRICE_COPY.nothingCharged}
    </Banner>
  );
}

/**
 * The authoring gate, drawn the way the domain lifecycle draws its states (R4.3).
 *
 * `DomainsCard.tsx:188-210` is the reference: a state pill for the state itself, the
 * server's own reason in words, and no invented verdict. Controls stay PRESENT and disabled
 * — an absent section reads as a missing feature, and the reader cannot tell the difference
 * between "not yet" and "never" (R4.2).
 *
 * Renders nothing when the gate holds, so a caller can place it unconditionally.
 */
export function PricingGateNotice({
  tenant,
  errorMessage,
}: {
  tenant: TenantPayments | null;
  /**
   * `last_error_message` from the tenant record, verbatim, for the `error` state (R4.4).
   *
   * Optional, and defaulted from the tenant itself below, because the seam already carries it:
   * a prop every caller had to remember to pass is a prop some caller forgets, and forgetting
   * this one silently drops the only sentence that says what is actually wrong.
   */
  errorMessage?: string | null;
}) {
  const notice = gateNotice(tenant);
  if (!notice) return null;

  const reason = errorMessage ?? tenant?.lastErrorMessage ?? null;

  return (
    <div className="pricing-gate">
      <span className="ui-pill" data-tone={tenant?.state === 'error' ? 'suspended' : 'planned'}>
        {notice.pill}
      </span>
      <p className="pricing-gate__note">
        {/* The tenant's OWN sentence first, where there is one, then ours about who to ask.
            Paraphrasing the server's message means maintaining a second copy of it. */}
        {tenant?.state === 'error' && reason ? `${reason} ` : null}
        {notice.note}
      </p>
    </div>
  );
}

/**
 * A row of the authoring summary (R6.7): a label, a value, and an optional explanation.
 *
 * `emphasis` marks the row the author is really asking about — what the learner is charged.
 * Seven rows rather than five is deliberate in the spec: an author setting a percentage needs
 * to see both the number the percentage applies to and the number the learner sees, and
 * collapsing them is how an author ends up surprised by their own price.
 */
export function SummaryRow({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="pricing-summary__row" data-emphasis={emphasis || undefined}>
      <dt>
        {label}
        {hint && <small>{hint}</small>}
      </dt>
      <dd>{value}</dd>
    </div>
  );
}
